/* ═══ Prospect → Quote conversion ═══
   Maps a website quote submission (quoteSubmissions doc, v2 adaptive brief)
   into builder quote state. Pure and read-only: nothing persists until the
   operator saves from the builder. Entry points: the "Start a quote in Suite"
   button in the inquiry email and the Convert button in Prospects, both via
   nycsuite.io/?convert=<sessionId>. */

import { BLANK_QUOTE } from "./utils.js";
import { INDUSTRY_LABELS } from "./constants.js";
import { emailDomain, resolveClient, resolveContact } from "./identity.js";
import { prospectDraftLineItems } from "./prospectLineItems.js";

const CAPTURE_LABELS = { people: "People", pets: "Pets", objects: "Objects", environments: "Environments" };

const humanize = (v) => String(v || "").replace(/_/g, " ");
// Current website submissions always store arrays, but legacy/corrupt records
// must degrade to empty rather than throw mid-conversion.
const arr = (v) => (Array.isArray(v) ? v : []);
const safeDecode = (s) => { try { return decodeURIComponent(s); } catch { return s; } };

// Photos live as image_ref blocks (tokened public Storage URLs) inside the
// cleaned conversationHistory the website API persists.
export function prospectPhotoAttachments(raw) {
  const urls = [];
  for (const msg of arr(raw.conversationHistory)) {
    if (!Array.isArray(msg?.content)) continue;
    for (const b of msg.content) {
      if (b?.type === "image_ref" && typeof b.url === "string") urls.push(b.url);
    }
  }
  return urls.map((url, i) => {
    const pm = /\/o\/([^?]+)/.exec(url);
    const storagePath = pm ? safeDecode(pm[1]) : null;
    const base = storagePath ? storagePath.split("/").pop() : "";
    return {
      id: `att_prospect_${raw._docId || raw.id || "x"}_${i}`,
      // Storage names carry an index prefix ("0_IMG_4893.jpeg") — display without it.
      name: (base || `photo_${i + 1}.jpg`).replace(/^\d+_/, ""),
      // Must be the normalized "image", not a raw MIME: aiReadableKind and the
      // image-cache filters gate on a strict === "image".
      type: "image",
      size: null,
      url,
      storagePath,
      aiRead: false,
      extracted: "",
      includeInAddendum: true,
      caption: "",
    };
  });
}

// Internal notes dossier: everything the operator needs while pricing that has
// no structured home on the quote (email/phone live on the CRM contact).
export function prospectNotes(raw) {
  const lines = [];
  const when = typeof raw.submittedAt === "string" ? ` — submitted ${raw.submittedAt.slice(0, 10)}` : "";
  lines.push(`Website inquiry${when}`);
  if (raw.contactEmail) lines.push(`Email: ${raw.contactEmail}`);
  if (raw.contactPhone) lines.push(`Phone: ${raw.contactPhone}`);
  if (raw.callMe) lines.push(`Client requested a callback.`);
  if (raw.leadSource) lines.push(`Heard about us: ${humanize(raw.leadSource)}`);

  const brief = typeof raw.projectBrief === "string" ? raw.projectBrief.trim() : "";
  if (brief) lines.push("", "Project brief:", brief);

  const items = arr(raw.briefItems).filter((b) => b && b.label && (b.value || b.value === 0));
  if (items.length) {
    lines.push("", "Questionnaire answers:");
    for (const b of items) lines.push(`- ${b.label}: ${b.value}${b.source === "inferred" ? " (inferred)" : ""}`);
  }

  const flags = arr(raw.feasibilityFlags);
  if (flags.length) {
    lines.push("", "Needs manual review:");
    for (const f of flags) lines.push(`- ${humanize(f)}`);
  }
  return lines.join("\n");
}

/**
 * @param raw   the quoteSubmissions doc
 * @param opts.contacts / opts.clients  current CRM, for resolving the submitter
 *
 * CD3 — the website already creates a proper CRM contact on submit
 * (`nyc_website/api/_lib/contacts.js` → ensureContact). What was lost was the
 * link: the submitter's email went into a prose notes blob and nowhere else, so
 * "Alex Morgan" returning as "Alex M." read as a brand-new person every time.
 * The email now travels as a structured field AND resolves to the contact the
 * website made, so a repeat submitter lands on their own record.
 */
export function mapProspectToQuote(raw, opts = {}) {
  const contactEmail = typeof raw.contactEmail === "string" ? raw.contactEmail.trim() : "";
  const contactPhone = typeof raw.contactPhone === "string" ? raw.contactPhone.trim() : "";
  const matchedContact = resolveContact({ email: contactEmail, name: raw.contactName }, opts.contacts).record;
  const matchedClient = resolveClient(
    { name: raw.company, domain: emailDomain(contactEmail) },
    opts.clients
  ).record;
  const types = arr(raw.captureTypes).map((t) => CAPTURE_LABELS[t] || t);
  const clientName = raw.company || raw.contactName || "";
  const isStudio = raw.location === "our_brooklyn_studio";
  // Deterministic draft pricing from the structured answers (approved 2026-08-06).
  // Every drafted line is a starting point the operator edits in the builder;
  // whatever couldn't be priced surfaces as flags in the internal notes.
  const draft = prospectDraftLineItems(raw);
  const notes = [
    prospectNotes(raw),
    draft.items.length ? "\nLine items were drafted automatically from the questionnaire answers — verify every line before sending." : "",
    draft.flags.length ? "\nPricing draft — needs manual attention:\n" + draft.flags.map((f) => `- ${f}`).join("\n") : "",
  ].filter(Boolean).join("\n");
  return {
    ...BLANK_QUOTE(),
    step: "review",
    clientName,
    contactName: raw.contactName || "",
    // Structured, not prose. The notes dossier keeps a human-readable copy
    // (prospectNotes) — it just stops being the only home for the data.
    contactEmail,
    contactPhone,
    contactEntityId: matchedContact?.id || null,
    companyEntityId: matchedClient?.id || null,
    // Website industry ids are the same 15-bucket taxonomy as INDUSTRY_LABELS.
    industry: INDUSTRY_LABELS[raw.industry] ? raw.industry : "other",
    leadSource: "website",
    jobLocation: isStudio ? "" : (raw.zipCode ? `ZIP ${raw.zipCode}` : ""),
    isStudio,
    scanDate: "",
    scanDateTBD: true,
    summary: types.length
      ? `${types.join(" + ")} capture${clientName ? ` for ${clientName}` : ""}`
      : `Website inquiry${clientName ? ` from ${clientName}` : ""}`,
    // internalNotes is the review-step-visible (and internal-only) field; the
    // notesText copy feeds the optional AI input step's extraction.
    internalNotes: notes,
    notesText: notes,
    // The inquiry text is the prospect's, not the operator's: a discount line in it never
    // applies at AI Start until the operator edits the notes (ai.js notesAreOperator).
    notesSource: "prospect",
    attachments: prospectPhotoAttachments(raw),
    lineItems: draft.items,
    sourceProspectId: raw._docId || raw.id || null,
    savedToDb: false,
  };
}
